/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React from 'react';
import { View, Text, TouchableOpacity, Platform, Linking } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import {
  getColor,
  getShape,
  getTextStyle,
  pd,
} from '@airasia-phoenix/react-native/utils/utils';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import LabelView from '@airasia-phoenix/react-native/components/LabelView';
import { LabelType } from '@airasia-phoenix/react-native/components/LabelView/types';
import { useTranslation } from 'react-i18next';
import { useFlagEnabled } from '@airasia-rn/account';
import { FEATURE_FLAG_HOME_ENABLE_ADS_RENDER } from '../../../constants';
import HomeMobileAd from '../../../widgets/MobileAds/HomeMobileAd';
import { HomeWebAd } from '../../../widgets/WebAds';
import type { RecommendationApiItem } from '../../../models/recommendTabResponse';

/** Space below square image for explore headline + CTA (native layout). */
const EXPLORE_COPY_STACK_HEIGHT = 50;

const titleStyle = getTextStyle(
  pd.sys.typescale.heading_4,
  pd.sys.colour.content_primary,
);
const ctaTextStyle = getTextStyle(
  pd.sys.typescale.caption_text_medium,
  pd.sys.colour.interactive_link,
);

export type AdCardViewProps = {
  item: RecommendationApiItem;
  cardSize: number;
  onAdFailed: (lobItemId: string) => void;
};

const AdCardView = ({ item, cardSize, onAdFailed }: AdCardViewProps) => {
  const { t } = useTranslation();
  const adUnitId =
    item.data?.primeAdsBanner?.adUnitId ??
    item.data?.primeAdsBanner?.adId ??
    '';
  const templateId = item.data?.primeAdsBanner?.templateId ?? '';

  const handleCtaPress = () => {
    if (item.deepLink) {
      void Linking.openURL(item.deepLink);
    }
  };

  const { enabled: isAdsRenderEnabled } = useFlagEnabled(
    FEATURE_FLAG_HOME_ENABLE_ADS_RENDER,
  );

  if (Platform.OS === 'web' && !isAdsRenderEnabled) {
    return null;
  }

  const showApiCopyBelow =
    Platform.OS === 'web' && (Boolean(item.title) || Boolean(item.subTitle));

  return (
    <View style={[styles.container, { width: cardSize }]}>
      {Platform.OS === 'web' ? (
        <View style={[styles.imageArea, { width: cardSize, height: cardSize }]}>
          <HomeWebAd
            adUnit={adUnitId}
            lobItemId={item.lobItemId}
            slotId={`ad-foryou-${item.lobItemId}`}
            onAdFailed={onAdFailed}
          />
          <View style={styles.adBadgeWrapper} pointerEvents="none">
            <LabelView
              type={LabelType.FILLED_MOVE}
              text={t('homePage.adBadge')}
              containerStyle={styles.adBadge}
              textStyle={styles.adBadgeText}
            />
          </View>
        </View>
      ) : (
        <HomeMobileAd
          adUnitId={adUnitId}
          lobItemId={item.lobItemId}
          templateId={templateId}
          width={cardSize}
          height={cardSize + EXPLORE_COPY_STACK_HEIGHT}
          collapseUntilVisible
          onAdFailed={onAdFailed}
          layoutType="explore"
        />
      )}

      {showApiCopyBelow && (
        <View style={styles.contentArea}>
          {Boolean(item.title) && (
            <Text style={[titleStyle, styles.title]} numberOfLines={2}>
              {item.title}
            </Text>
          )}

          {Boolean(item.subTitle) && (
            <TouchableOpacity
              style={styles.ctaRow}
              onPress={handleCtaPress}
              activeOpacity={0.7}
            >
              <Text style={ctaTextStyle}>{item.subTitle}</Text>
              <IconView
                style={styles.ctaIcon}
                width={16}
                height={16}
                tint={getColor(pd.sys.colour.interactive_link)}
                icon={pd.icon.main.navigation.get('arrow_forward_ios')}
              />
            </TouchableOpacity>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create((pd) => ({
  container: {
    flex: 1,
    alignItems: 'flex-start',
    padding: pd.ref.space.spacing10.px,
  },
  imageArea: {
    ...getShape(pd.comp.content_card.image.shape),
    overflow: 'hidden',
    backgroundColor: getColor(pd.sys.colour.container_low),
  },
  adBadgeWrapper: {
    position: 'absolute',
    alignSelf: 'flex-start',
    paddingVertical: pd.ref.space.spacing20.px,
    paddingHorizontal: pd.ref.space.spacing20.px,
  },
  contentArea: {
    marginTop: pd.ref.space.spacing20.px,
    paddingRight: pd.ref.space.spacing10.px,
    alignItems: 'flex-start',
  },
  title: {
    marginBottom: pd.ref.space.spacing10.px,
  },
  ctaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: pd.ref.space.spacing10.px,
  },
  ctaIcon: {
    marginTop: 1,
  },
  adBadge: {
    backgroundColor: getColor(pd.sys.colour.content_primary),
  },
  adBadgeText: {
    color: getColor(pd.sys.colour.inline_white),
  },
}));

export default AdCardView;
